import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Typography, Box, CircularProgress } from '@mui/material';
import { getEventResponses, EventResponse } from '../../services/firebaseService';
import { FileDownload } from '@mui/icons-material';

// Dummy export function (replace with real Excel export logic)
const exportResponsesToExcel = (responses: EventResponse[], eventName: string) => {
  // You can use a library like xlsx or SheetJS for real export
  const csv = [
    ['Name', 'Email', 'LinkedIn', 'Country', 'Speciality', 'Submitted'],
    ...responses.map(r => [
      r.name, r.email, r.linkedin, r.country, r.speciality, r.submittedAt?.toDate ? r.submittedAt.toDate().toLocaleString() : ''
    ])
  ].map(row => row.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${eventName || 'event'}-responses.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const EventResponsesPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [responses, setResponses] = useState<EventResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [eventName, setEventName] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchResponses = async () => {
      setLoading(true);
      try {
        if (!eventId) return;
        // Fetch event name (optional, for export file name)
        const eventDoc = await import('../../firebase').then(({ db }) =>
          import('firebase/firestore').then(({ doc, getDoc }) => getDoc(doc(db, 'events', eventId)))
        );
        if (eventDoc && eventDoc.exists()) {
          setEventName(eventDoc.data().name || 'Event');
        }
        const res = await getEventResponses(eventId);
        setResponses(res);
      } finally {
        setLoading(false);
      }
    };
    fetchResponses();
  }, [eventId]);

  return (
    <Box sx={{ p: 3, background: '#f7f9fb', minHeight: '100vh', color: '#222428' }}>
      <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Back
      </Button>
      <Typography variant="h4" sx={{ color: '#222428', mb: 2 }}>{eventName} - Responses</Typography>
      <Button
        variant="contained"
        color="primary"
        startIcon={<FileDownload />}
        onClick={() => exportResponsesToExcel(responses, eventName)}
        sx={{ mb: 2 }}
        disabled={responses.length === 0}
      >
        Export Responses
      </Button>
      {loading ? (
        <CircularProgress />
      ) : responses.length === 0 ? (
        <Typography>No responses yet.</Typography>
      ) : (
        <TableContainer sx={{ background: 'transparent', color: '#222428' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: '#222428' }}>Name</TableCell>
                <TableCell sx={{ color: '#222428' }}>Email</TableCell>
                <TableCell sx={{ color: '#222428' }}>LinkedIn</TableCell>
                <TableCell sx={{ color: '#222428' }}>Country</TableCell>
                <TableCell sx={{ color: '#222428' }}>Speciality</TableCell>
                <TableCell sx={{ color: '#222428' }}>Submitted</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {responses.map(resp => (
                <TableRow key={resp.id}>
                  <TableCell sx={{ color: '#222428' }}>{resp.name}</TableCell>
                  <TableCell sx={{ color: '#222428' }}>{resp.email}</TableCell>
                  <TableCell sx={{ color: '#222428' }}>{resp.linkedin}</TableCell>
                  <TableCell sx={{ color: '#222428' }}>{resp.country}</TableCell>
                  <TableCell sx={{ color: '#222428' }}>{resp.speciality}</TableCell>
                  <TableCell sx={{ color: '#222428' }}>{resp.submittedAt?.toDate ? resp.submittedAt.toDate().toLocaleString() : ''}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default EventResponsesPage; 